"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

import {
  captureActivationAttribution,
  readActivationAttribution,
} from "@/utils/activationAttribution";
import {
  isPublicAnalyticsPath,
  stripAnalyticsLocale,
} from "@/utils/analyticsPrivacy";

const SYNCED_SESSION_KEY = "ariaflow:activation-attribution-synced:v1";

export default function ActivationAttributionManager({
  locale,
}: {
  locale: string;
}): null {
  const pathname = usePathname();
  const capturedLanding = useRef(false);

  useEffect(() => {
    const path = stripAnalyticsLocale(pathname);
    const isAcquisitionPage =
      isPublicAnalyticsPath(pathname) || path === "/signup" || path === "/login";

    if (isAcquisitionPage && !capturedLanding.current) {
      capturedLanding.current = true;
      try {
        captureActivationAttribution({
          storage: window.localStorage,
          url: window.location.href,
          referrer: document.referrer,
          locale,
        });
      } catch {
        // Attribution is diagnostic and must never block the product journey.
      }
    }

    if (!path.startsWith("/dashboard")) return;

    const attribution = readActivationAttribution(window.localStorage);
    if (!attribution) return;
    const fingerprint = [
      attribution.visitorId,
      attribution.firstTouch.capturedAt,
      attribution.lastTouch.capturedAt,
      attribution.lastTouch.ctaLocation,
    ].join(":");
    if (window.sessionStorage.getItem(SYNCED_SESSION_KEY) === fingerprint) return;

    void fetch("/api/activation/attribution", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      keepalive: true,
      body: JSON.stringify({ attribution }),
    })
      .then((response) => {
        if (response.ok) {
          window.sessionStorage.setItem(SYNCED_SESSION_KEY, fingerprint);
        }
      })
      .catch((): undefined => undefined);
  }, [locale, pathname]);

  return null;
}
